import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Carrier Dispatch Agreement modal.
 *
 * Props:
 *   open               (bool)              — controlled open state
 *   onOpenChange       (fn)                — controlled state setter
 *   email              (string)            — carrier email (used to record signature)
 *   defaultName        (string, optional)  — pre-fill the legal name
 *   defaultMcNumber    (string, optional)
 *   onSigned           (fn)                — invoked after successful sign with the response payload
 *   blocking           (bool, optional)    — if true, the close button is hidden (e.g. blocking Autopilot toggle)
 */
const CarrierAgreementModal = ({ open, onOpenChange, email, defaultName = '',
                                defaultMcNumber = '', onSigned, blocking = false }) => {
  const [agreementText, setAgreementText] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [legalName, setLegalName] = useState(defaultName);
  const [mcNumber, setMcNumber] = useState(defaultMcNumber);
  const [signature, setSignature] = useState('');
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/carrier/agreement/text`)
      .then((r) => setAgreementText(r.data))
      .catch(() => toast.error('Failed to load agreement text'));
  }, [open]);

  useEffect(() => {
    if (open) {
      setLegalName(defaultName); setMcNumber(defaultMcNumber);
      setAccepted(false); setSignature('');
    }
  }, [open, defaultName, defaultMcNumber]);

  const submit = async () => {
    if (!accepted) return toast.error('You must check the agreement box');
    if (!legalName.trim()) return toast.error('Legal name required');
    if (signature.trim().toLowerCase() !== legalName.trim().toLowerCase()) {
      return toast.error('Typed signature must match your legal name exactly');
    }
    setSigning(true);
    try {
      const r = await axios.post(`${API}/carrier/agreement/sign`, {
        email, name: legalName.trim(),
        mc_number: mcNumber || undefined,
        typed_signature: signature.trim(),
      });
      toast.success('Agreement signed');
      onSigned?.(r.data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to sign agreement');
    } finally {
      setSigning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={blocking ? () => {} : onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="carrier-agreement-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-400" />
            {agreementText?.title || 'Carrier Dispatch Agreement'}
          </DialogTitle>
          <DialogDescription>
            {agreementText?.preamble || 'Please review and sign before enabling Autopilot.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <ul className="list-decimal pl-5 space-y-1 text-foreground/90 max-h-56 overflow-y-auto"
              data-testid="agreement-clauses">
            {(agreementText?.clauses || []).map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>

          <div className="flex items-center gap-2 pt-2">
            <Checkbox id="agreement-accept" checked={accepted}
                      onCheckedChange={setAccepted} data-testid="agreement-accept-checkbox" />
            <Label htmlFor="agreement-accept" className="text-sm cursor-pointer">
              I have read and agree to the Carrier Dispatch Agreement above.
            </Label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Legal name</Label>
              <Input value={legalName} onChange={(e) => setLegalName(e.target.value)}
                     placeholder="John Q Carrier" data-testid="agreement-legal-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">MC number (optional)</Label>
              <Input value={mcNumber} onChange={(e) => setMcNumber(e.target.value)}
                     placeholder="MC-123456" data-testid="agreement-mc-number" />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Digital signature — type your legal name exactly</Label>
            <Input value={signature} onChange={(e) => setSignature(e.target.value)}
                   placeholder="John Q Carrier"
                   className="font-serif italic text-base"
                   data-testid="agreement-signature" />
          </div>

          <p className="text-[11px] text-muted-foreground">
            Version {agreementText?.version || '—'} · Signed timestamp and IP recorded for audit.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {!blocking && (
            <Button variant="outline" onClick={() => onOpenChange?.(false)}
                    data-testid="agreement-cancel-btn">
              Cancel
            </Button>
          )}
          <Button onClick={submit} disabled={signing || !accepted || !legalName.trim() || !signature.trim()}
                  data-testid="agreement-sign-btn">
            {signing
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing…</>
              : 'Sign agreement'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CarrierAgreementModal;
